import React from "react";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import { lastNine } from "../employeeProductionHelpers";
import {
  DropdownOption,
  EmployeeProductionDialog,
  ReceivingItem,
  SaveScannedItemPayload,
  UpdateScannedItemPayload,
} from "../employeeProductionTypes";
import { ConfirmScannedAmountDialog } from "./ConfirmScannedAmountDialog";
import { EditScannedItemDialog } from "./EditScannedItemDialog";
import { ScannedItemActionsDialog } from "./ScannedItemActionsDialog";

type Props = {
  dialog: EmployeeProductionDialog;
  selectedLot: DropdownOption | null;
  onClose: () => void;
  onConfirmScan: (payload: SaveScannedItemPayload) => Promise<void>;
  onConfirmEdit: (payload: UpdateScannedItemPayload) => Promise<void>;
  onConfirmDelete: () => Promise<void>;
  onEdit: (item: ReceivingItem) => void;
  onDelete: (item: ReceivingItem) => void;
};

export function EmployeeProductionDialogContent({
  dialog,
  selectedLot,
  onClose,
  onConfirmScan,
  onConfirmEdit,
  onConfirmDelete,
  onEdit,
  onDelete,
}: Props): React.ReactNode {
  const setOpen = (_value: unknown) => onClose();

  switch (dialog.type) {
    case "none":
      return null;
    case "error":
      return (
        <ModelErrorComponent
          setOpen={setOpen}
          title={dialog.title}
          subtitle={dialog.message}
        />
      );
    case "confirm-scan":
      if (!selectedLot) return null;
      return (
        <ConfirmScannedAmountDialog
          item={dialog.item}
          productionId={selectedLot.value}
          onConfirm={onConfirmScan}
          suggestedAmount={dialog.suggestedAmount}
          onClose={onClose}
        />
      );
    case "item-actions":
      return (
        <ScannedItemActionsDialog
          item={dialog.item}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      );
    case "edit-item":
      if (!selectedLot) return null;
      return (
        <EditScannedItemDialog
          item={dialog.item}
          productionId={selectedLot.value}
          onConfirm={onConfirmEdit}
          onClose={onClose}
        />
      );
    case "delete-item": {
      const amount = dialog.item.ingredient_amount_used;
      const unit = dialog.item.unit_of_measure ?? "";
      return (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={onConfirmDelete}
          message={`Are you sure you want to delete ${lastNine(dialog.item.barcode)}?${amount > 0 ? ` (Amount: ${amount} ${unit})` : ""}`}
          setOpen={setOpen}
          title="Delete Scanned Item"
        />
      );
    }
  }
}
